import React, { useEffect, useRef, useState } from "react";
import {
  FaChevronDown,
  FaChevronRight,
  FaChevronUp,
  FaTimes,
} from "react-icons/fa";
import DebugConsole from "./DebugConsole";
import DebuggerStateManager from "./DebugStateManager";
import PromptProblemsTab from "./PromptProblemsTab";
import PromptWriter from "./PromptWriter";
import PromptPreview from "./preview/PromptPreview";
import ReleaseNoteTab from "./ReleaseNoteTab";
import TemplateEditor from "./TemplateEditor";
import PromptFragmentsTab from "./PromptFragmentsTab";
import TestConfigTab from "./TestConfigTab";

const tabNames = {
  templates: "TEMPLATE",
  fragments: "FRAGMENTS",
  configs: "DEBUG CONFIGS",
  problems: "PROBLEMS",
  console: "DEBUG",
  // output: "OUTPUT", <-- turn off for now
  versions: "COMMITS",
};

const PromptTabs = ({
  initialCode,
  selectedPath,
  visibleTabs,
  onSelectPath,
  onHideTab,
  onContentChange,
  onFragmentAdd,
  readOnly = false,
  promptKey,
  promptType,
  language = null,
  promptFragments = [],
  testConfigs = [],
  onTestConfigUpdate,
  onTestConfigAdd,
}) => {
  const tabsContainerRef = useRef(null);
  const editorRef = useRef(null);
  const [isTerminalOpen, setIsTerminalOpen] = useState(false);
  const [terminalHeight, setTerminalHeight] = useState(
    Math.max(300, window.innerHeight / 3)
  );
  const [isDragging, setIsDragging] = useState(false);
  const [isHoveringHandle, setIsHoveringHandle] = useState(false);
  const [selectedTab, setSelectedTab] = useState("console");
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    const setTabAfterSave = (e) => {
      setSelectedTab("versions");
    };
    window.addEventListener("refreshPromptTabs", setTabAfterSave);
    return () => {
      window.removeEventListener("refreshPromptTabs", setTabAfterSave);
    };
  }, [setSelectedTab]);

  useEffect(() => {
    const checkTabOverflow = () => {
      if (tabsContainerRef.current) {
        const container = tabsContainerRef.current;
        const isOverflowing = container.scrollWidth > container.clientWidth;
        if (isOverflowing && visibleTabs.length > 1) {
          onHideTab(visibleTabs[visibleTabs.length - 1]);
        }
      }
    };
    checkTabOverflow();
    const handleResize = () => {
      checkTabOverflow();
    };
    window.addEventListener("resize", handleResize);
    window.addEventListener("openTerminal", handleOpenTerminal);
    window.addEventListener("openTemplate", handleOpenTemplate);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("openTerminal", handleOpenTerminal);
      window.removeEventListener("openTemplate", handleOpenTemplate);
    };
  }, [visibleTabs, onHideTab]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const newHeight = window.innerHeight - e.clientY;
        setTerminalHeight(
          Math.max(100, Math.min(newHeight, window.innerHeight - 100))
        );
      }
    };
    const handleMouseUp = () => {
      setIsDragging(false);
    };
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  const handleEditorChange = (value) => {
    if (!value) value = " "; // HACK, keeps editor from hiding
    onContentChange(selectedPath, value);
  };

  const handleTabClick = (path) => {
    onSelectPath(path);
  };

  const handleHideTab = (e, path) => {
    e.stopPropagation();
    onHideTab(path);
  };

  const handleFragmentAdded = (fragmentReference) => {
    onFragmentAdd(fragmentReference);
  };

  const getNameFromPath = (path) => {
    const parts = path.split("/");
    return parts[parts.length - 1];
  };

  const renderBreadcrumbs = (path) => {
    const parts = path.split("/").splice(1);
    return (
      <div className="flex items-center text-sm text-gray-400 pl-2 h-8 bg-[#1E1E1E]">
        {parts.map((part, index) => (
          <React.Fragment key={index}>
            {index > 0 && <FaChevronRight className="mx-2" size={10} />}
            <span>{part}</span>
          </React.Fragment>
        ))}
      </div>
    );
  };

  const toggleTerminal = () => {
    setIsTerminalOpen(!isTerminalOpen);
  };

  const handleDoubleClick = () => {
    setIsTerminalOpen(!isTerminalOpen);
  };

  const handleOpenTerminal = (e) => {
    setSelectedTab("console");
    setIsTerminalOpen(true);
  };

  const handleOpenTemplate = (e) => {
    setSelectedTab("templates");
    setIsTerminalOpen(true);
    setSelectedTemplate(e.detail);
  };

  return (
    <div className="h-[calc(100vh-68px)] flex flex-col relative">
      {/* Tabs */}
      <div ref={tabsContainerRef} className="flex overflow-x-auto">
        {visibleTabs.map((path) => (
          <button
            key={path}
            className={`
              text-xs flex items-center whitespace-nowrap px-3 py-1.5 mr-1
              ${
                selectedPath === path
                  ? "text-blue-300 bg-[#1E1E1E] border-t-2 border-blue-500"
                  : "text-gray-400 hover:bg-gray-700 hover:text-gray-200"
              }
            `}
            onClick={() => handleTabClick(path)}
            title={path}
          >
            {getNameFromPath(path)}
            <FaTimes
              className="ml-2 text-gray-500 hover:text-gray-300"
              onClick={(e) => handleHideTab(e, path)}
            />
          </button>
        ))}
      </div>

      {/* Breadcrumbs */}
      {renderBreadcrumbs(selectedPath)}

      {/* Main content area with PromptWriter */}
      <div className="flex-grow overflow-hidden">
        {selectedPath &&
          initialCode[selectedPath] &&
          (language === "markdown" ? (
            <PromptPreview value={initialCode[selectedPath]} />
          ) : (
            <PromptWriter
              value={initialCode[selectedPath]}
              language={
                language !== null
                  ? language
                  : selectedPath.startsWith("config/launch")
                  ? "yaml"
                  : "jinja"
              }
              onChange={handleEditorChange}
              readOnly={readOnly}
              ref={editorRef}
            />
          ))}
      </div>

      {/* Terminal */}
      <div
        className={`absolute bottom-[-12px] left-0 right-0 transition-all duration-300 ease-in-out z-[1000] ${
          isTerminalOpen ? "" : "h-12"
        }`}
        style={{ height: isTerminalOpen ? `${terminalHeight}px` : "48px" }}
      >
        {/* Terminal header */}
        <div className="flex items-center justify-between py-2 px-3 bg-[rgb(23,23,23)] relative">
          {/* Blue highlight bar */}
          <div
            className={`absolute top-0 left-0 right-0 h-1 cursor-ns-resize transition-opacity duration-300 ${
              isHoveringHandle
                ? "bg-blue-500 opacity-100"
                : "bg-transparent opacity-0"
            }`}
            onMouseEnter={() => setIsHoveringHandle(true)}
            onMouseLeave={() => setIsHoveringHandle(false)}
            onMouseDown={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDoubleClick={handleDoubleClick}
          />
          <div className="flex text-xs space-x-12 py-.5 mx-4 ">
            {Object.entries(tabNames).map(([key, value]) => (
              <button
                key={key}
                className={
                  selectedTab === key
                    ? "text-blue-300 border-b-2 border-blue-300"
                    : "text-gray-300"
                }
                onClick={() => {
                  setSelectedTab(key);
                  setIsTerminalOpen(true);
                }}
              >
                {value}
              </button>
            ))}
          </div>
          <button
            onClick={toggleTerminal}
            className="text-gray-300 text-sm hover:text-gray-300 focus:outline-none"
          >
            {isTerminalOpen ? <FaChevronDown /> : <FaChevronUp />}
          </button>
        </div>

        {/* Terminal content */}
        <DebuggerStateManager />
        {
          <div
            className={`bg-[#1E1E1E] overflow-auto dark-slim-scrollbar ${
              isTerminalOpen ? "h-[calc(100%-48px)] " : "h-0"
            }`}
          >
            {selectedTab === "templates" && (
              <TemplateEditor
                key={selectedTemplate?.path || "default"}
                path={selectedTemplate?.path}
                initialValue={selectedTemplate?.value}
              />
            )}
            {selectedTab === "fragments" && (
              <PromptFragmentsTab
                editor={editorRef}
                onFragmentAdded={handleFragmentAdded}
                currentPromptFragments={promptFragments}
              />
            )}
            {selectedTab === "configs" && (
              <TestConfigTab
                currentTestConfigs={testConfigs}
                onTestConfigAdd={onTestConfigAdd}
                onTestConfigSave={onTestConfigUpdate}
              />
            )}
            {selectedTab === "problems" && (
              <PromptProblemsTab
                prompt={initialCode[selectedPath]}
                language={language}
              />
            )}
            {selectedTab === "console" && <DebugConsole />}
            {selectedTab === "versions" && (
              <ReleaseNoteTab promptKey={promptKey} promptType={promptType} />
            )}
          </div>
        }
      </div>
    </div>
  );
};

export default PromptTabs;
